import React from 'react';
import { Typography, Box, IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { themeBackgrounds } from '../theme/theme';
import { useNavigate } from 'react-router-dom';

interface PageHeaderProps {
  heading: string;
  subheading?: string;
  children?: React.ReactNode;
  variant?: 'gradient' | 'vibrant' | 'ocean' | 'sunset' | 'forest';
}

const PageHeader: React.FC<PageHeaderProps> = ({ 
  heading, 
  subheading, 
  children, 
  variant = 'gradient' 
}) => {
  const navigate = useNavigate();
  const getVariantStyles = () => {
    const variants = {
      gradient: {
        background: themeBackgrounds.default,
        color: '#ffffff',
      },
      vibrant: {
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: '#ffffff',
      },
      ocean: {
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        color: '#ffffff',
      },
      sunset: {
        background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        color: '#ffffff',
      },
      forest: {
        background: 'linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)',
        color: '#ffffff',
      }
    };
    return variants[variant];
  };

  const variantStyle = getVariantStyles();

  return (
    <Box 
      sx={{ 
        mb: 4,
        p: 3,
        borderRadius: 2,
        ...variantStyle,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          zIndex: 0,
        },
        '& > *': {
          position: 'relative',
          zIndex: 1,
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: 'inherit',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
              width: 40,
              height: 40,
            }}
            aria-label="Go back"
          >
            <ArrowBack />
          </IconButton>
        <Typography 
          variant="h4" 
          gutterBottom={false}
          sx={{ 
            fontWeight: 700,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            letterSpacing: '-0.025em',
            flex: 1,
          }}
        >
          {heading}
        </Typography>
      </Box>
      {subheading && (
        <Typography 
          variant="body1" 
          sx={{ 
            opacity: 0.9,
            fontSize: '1.1rem',
            lineHeight: 1.6,
            mt: 1,
          }}
        >
          {subheading}
        </Typography>
      )}
      {children && (
        <Box sx={{ mt: 2 }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;